import {LeaderLineLabeler} from "@esWebAPI/es/map/module/LeaderLineLabeler";

import type {Feature} from "geojson";
import type {Dimensions, LeaderLineConfig} from "@esWebAPI/es/model/Models";

/**
 * Calculate Label Dimensions based on Feature (attrs) and LeaderLineConfig (label styling), used for collision-detection.
 */
export class LeaderLineLabelDimensionCalculator {

    private readonly canvas = document.createElement("canvas");
    private readonly context = this.canvas.getContext("2d") as CanvasRenderingContext2D;

    // only enable one of these at a time
    private readonly DEBUG_SHOW_LABEL_MEASURING_CANVAS = false;
    private readonly DEBUG_SHOW_LINE_MEASURING_CANVAS = true;

    constructor() {
        if (this.DEBUG_SHOW_LABEL_MEASURING_CANVAS) {
            document.getElementById("map-wrapper")?.append(this.canvas);
            this.canvas.width = 500;
            this.canvas.height = 100;
            this.canvas.style.position = "absolute";
            this.canvas.style.top = "300px";
        }
    }

    /**
     * Calculate the label size in Dimensions (width, height)
     * @param feature feature to label
     * @param llConfig
     * @param mlLayer maplibre layer (not sure what exact TS type is) containing the feats we want to label
     */
    public getLabelDimensions(feature: Feature<any,any>, llConfig: LeaderLineConfig, mlLayer: any): Dimensions {

        // Resolve the "text-field" expression for the feature.
        const text = mlLayer.getValueAndResolveTokens('text-field', feature, {}, null);

        if(!text || text.length === 0 || text.sections.length == 1 && text.sections[0]?.text === ""){
            // should never happen but if it does it gives us a clue on the map and dev console
            es.log.warn("getLabelDimensions : Could not get text for LLL. Guessing", feature, llConfig, text);
            return LeaderLineLabeler.AVERAGE_LABEL_DIMENSIONS;
        }

        const textStyle = llConfig.mapLayer.layerView?.styleSettings?.defaultStyle?.text;
        if(!textStyle){
            es.log.error("could not get textStyle from llConfig.mapLayer", llConfig.mapLayer);
            return;
        }
        const fontSize = +mlLayer.getLayoutProperty('text-size');
        const font = textStyle.family;

        // Get the text font size from the layer options.
        this.context.font = `${textStyle.italic ? 'italic ' : ''}${fontSize}px ${font}`;

        // Process text by lines
        const lines: string[] = [];
        let currentLine = '';

        // First, separate text into logical lines based on explicit newlines
        text.sections.forEach((section: any) => {
            if (!section.text) return;

            // Handle explicit newlines in the section
            const parts = section.text.split('\n');
            parts.forEach((part: string, index: number) => {
                if (index > 0) {
                    // If this isn't the first part, it means there was a newline
                    lines.push(currentLine);
                    currentLine = part;
                } else {
                    currentLine += part;
                }
            });

            // If the section ends with a newline, add the current line and reset
            if (section.text.endsWith('\n')) {
                lines.push(currentLine);
                currentLine = '';
            }
        });

        // Add any remaining text as the final line
        if (currentLine) {
            lines.push(currentLine);
        }

        // Handle case where no lines were created (e.g., only had empty text with newlines)
        if (lines.length === 0) {
            lines.push('');
        }

        // Measure each line to find the widest one
        let maxLineWidth = 0;
        lines.forEach(line => {
            const lineWidth = this.context.measureText(line).width;
            maxLineWidth = Math.max(maxLineWidth, lineWidth);
        });

        // Check if the lines need to be wrapped due to max width constraint
        const maxWidth = LeaderLineLabeler.MAX_LABEL_WIDTH;
        const wrappedLines: string[] = [];

        lines.forEach(line => {
            if (this.context.measureText(line).width <= maxWidth) {
                wrappedLines.push(line);
            } else {
                // This is a basic word-wrap implementation
                const words = line.split(' ');
                let currentWrappedLine = '';

                words.forEach(word => {
                    const testLine = currentWrappedLine ? `${currentWrappedLine} ${word}` : word;
                    const testLineWidth = this.context.measureText(testLine).width;

                    if (testLineWidth <= maxWidth) {
                        currentWrappedLine = testLine;
                    } else {
                        wrappedLines.push(currentWrappedLine);
                        currentWrappedLine = word;
                    }
                });

                if (currentWrappedLine) {
                    wrappedLines.push(currentWrappedLine);
                }
            }
        });

        // Get metrics for height calculation
        const metrics = this.context.measureText('M');
        const singleLineHeight = metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;

        // Calculate the final dimensions
        const BASE_PADDING = 2;  // base padding for all labels
        const BOX_EXTRA_PADDING = 2; // extra padding for box labels
        const MULTI_LINE_EXTRA_PADDING = 2; // extra padding for multi lines
        const isLabelBoxEnabled = !!textStyle?.boxFillColor;

        const totalPadding = BASE_PADDING +
            (isLabelBoxEnabled ? BOX_EXTRA_PADDING : 0) +
            (wrappedLines.length > 1 ? MULTI_LINE_EXTRA_PADDING : 0);

        // Use actual measured width, capped at max width
        const width = Math.min(maxLineWidth, maxWidth) + (totalPadding * 2);
        const height = (singleLineHeight * wrappedLines.length) + (totalPadding * 2);

        if (this.DEBUG_SHOW_LABEL_MEASURING_CANVAS) {
            this.context.fillText(`${width}x${height}`, 5, fontSize * (feature.index || 1));
        }

        if (this.DEBUG_SHOW_LINE_MEASURING_CANVAS) {
            wrappedLines.forEach((line, idx) => {
                this.context.fillText(`${idx}: ${line}`, 5, fontSize * (feature.index || 1) + 20 + (idx * 15));
            });
        }

        //es.log.log("getLabelDimensions", feature, llConfig, {width, height});

        return {width, height};
    }

    public clearContext(): void {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
}
